// EAA-1667: Core theme engine, parameterized copy of web-content's ThemeProvider.
// Moved into packages/hooks so every app shares one theming core (mode state,
// localStorage, the app→instance→tenant cascade, tenant-scoped cache). The app-
// specific inputs — which preset/instance layers, the tenant id used for cache
// scoping, and the antd config / override-layering helpers — are injected as props
// instead of read from Redux or app-local modules, keeping this package free of any
// app or scope:domain dependency. It deliberately does NOT mount packages/ui's
// UIColorsProvider (that would make packages/hooks ↔ packages/ui circular); the app
// adapter bridges `useColors()` into UIColorsProvider instead.
import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useMemo,
} from 'react';
import { theme as antdTheme, type ThemeConfig } from 'antd';
import {
    applyTheme,
    mergeThemeLayers,
    sanitizeThemeConfig,
    cacheCustomTheme,
    getCachedCustomTheme,
    getColors,
    setThemeModeAttributes,
    type CustomThemeConfig,
    type CustomThemeColors,
    type Colors,
    type ThemeLayers,
} from '@ai-content-hub/shared-styles';

export type ThemeMode = 'light' | 'dark';

export interface CoreThemeContextValue {
    mode: ThemeMode;
    toggleTheme: () => void;
    setTheme: (mode: ThemeMode) => void;
    isDark: boolean;
    colors: Colors;
    /** Antd ThemeConfig for the current cascade (algorithm + tokens). */
    antdTheme: ThemeConfig;
    setCustomThemeConfig: (config: CustomThemeConfig | null) => void;
}

const ThemeContext = createContext<CoreThemeContextValue | undefined>(undefined);

const THEME_STORAGE_KEY = 'app-theme-mode';

export interface CoreThemeProviderProps {
    children: React.ReactNode;
    /** App preset layer — base of the cascade (app-specific). */
    appPreset: ThemeLayers['appPreset'];
    /** Instance-default layer — middle of the cascade (deploy-specific). */
    instance: ThemeLayers['instance'];
    /** Tenant id the tenant-override layer is cached against (from the app store). */
    tenantId?: string | null;
    /** App's Antd ThemeConfig generator from the merged Colors. */
    getThemeConfig: (colors: Colors) => ThemeConfig;
    /** App's white-label override layerer, applied on top of base Colors. */
    applyOverridesToColors: (base: Colors, overrides: Partial<CustomThemeColors>) => Colors;
}

export const CoreThemeProvider: React.FC<CoreThemeProviderProps> = ({
    children,
    appPreset,
    instance,
    tenantId,
    getThemeConfig,
    applyOverridesToColors,
}) => {
    const [mode, setMode] = useState<ThemeMode>(() => {
        const stored = localStorage.getItem(THEME_STORAGE_KEY) as ThemeMode;
        if (stored === 'light' || stored === 'dark') return stored;
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    });

    // Initialize from localStorage cache so we don't flash default colors.
    // The blocking <script> in index.html already applied these CSS vars;
    // initializing here keeps React's state in sync from the first render.
    const [customConfig, setCustomConfig] = useState<CustomThemeConfig | null>(
        () => getCachedCustomTheme(tenantId)
    );

    const isDark = mode === 'dark';

    // EAA-1664: the full cascade for this session — app preset -> instance
    // default -> tenant override (most specific wins). `customConfig` is the
    // tenant layer; it's sanitized inside mergeThemeLayers/applyTheme.
    const themeLayers = useMemo<ThemeLayers>(
        () => ({ appPreset, instance, tenant: customConfig }),
        [appPreset, instance, customConfig]
    );

    // Merge cascade layers into the base Colors object (for Ant Design)
    const colors = useMemo(() => {
        const base = getColors(isDark);
        const merged = mergeThemeLayers(themeLayers, mode);
        if (Object.keys(merged.colors).length === 0) return base;
        return applyOverridesToColors(base, merged.colors);
    }, [isDark, themeLayers, mode, applyOverridesToColors]);

    // EAA-755: stamp BOTH markers, not just `data-theme`. Tailwind's
    // `darkMode: ['class']` — what content-kit, content-elements and
    // content-core all configure — matches a `.dark` ancestor, so while only
    // the attribute was written every `dark:` utility in those packages was
    // dead code and content-type players rendered their light palette inside
    // a dark shell.
    useEffect(() => {
        localStorage.setItem(THEME_STORAGE_KEY, mode);
        setThemeModeAttributes(mode);
    }, [mode]);

    // EAA-1664 fix (Fable 5 review): track which tenantId the current
    // `customConfig` was actually set for. `setCustomThemeConfig` is only
    // ever called (by the app's useCustomTheme) once a fetch for the *current*
    // tenant resolves, so stamping the tenantId at that call site — not reading
    // it back out of the effect below — is what lets the cache-write guard
    // detect a tenant switch that's still in flight (customConfig stale) and
    // skip writing the outgoing tenant's theme into the new tenant's cache slot.
    const configTenantIdRef = React.useRef(tenantId);

    // Apply the full cascade — clear+apply is synchronous within one effect
    // callback (no paint can occur in between), so this runs on every mount
    // and update, including the first: the bootstrap script in index.html only
    // ever pre-applies the tenant cache slot, never the app-preset/instance
    // layers, so skipping this on first mount would leave those layers (and a
    // boot-time mode/cache mismatch) unapplied until some later, unrelated
    // re-render.
    useEffect(() => {
        applyTheme(themeLayers, mode);

        if (configTenantIdRef.current === tenantId) {
            cacheCustomTheme(sanitizeThemeConfig(customConfig), tenantId);
        }
    }, [themeLayers, customConfig, mode, tenantId]);

    const toggleTheme = useCallback(() => {
        setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
    }, []);

    const setTheme = useCallback((newMode: ThemeMode) => {
        setMode(newMode);
    }, []);

    const setCustomThemeConfig = useCallback(
        (config: CustomThemeConfig | null) => {
            configTenantIdRef.current = tenantId;
            setCustomConfig(config);
        },
        [tenantId]
    );

    const antdThemeConfig = useMemo<ThemeConfig>(
        () => ({
            algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
            ...getThemeConfig(colors),
        }),
        [isDark, colors, getThemeConfig]
    );

    const value = useMemo<CoreThemeContextValue>(
        () => ({
            mode,
            toggleTheme,
            setTheme,
            isDark,
            colors,
            antdTheme: antdThemeConfig,
            setCustomThemeConfig,
        }),
        [mode, toggleTheme, setTheme, isDark, colors, antdThemeConfig, setCustomThemeConfig]
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

/**
 * Access theme mode, colors, and controls. Must be used within a
 * `CoreThemeProvider` (mounted by the app's theme adapter).
 */
export const useTheme = (): CoreThemeContextValue => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a CoreThemeProvider');
    }
    return context;
};

/**
 * Theme-aware colors. Colors automatically switch based on light/dark mode.
 *
 * @example
 * const colors = useColors();
 * <div style={{ color: colors.text, background: colors.background }}>
 */
export const useColors = (): Colors => {
    const { colors } = useTheme();
    return colors;
};

/**
 * Antd theme configuration for the current cascade (algorithm + tokens),
 * computed by the app's injected `getThemeConfig`.
 */
export const useAntdTheme = (): ThemeConfig => {
    const { antdTheme: config } = useTheme();
    return config;
};
